import useSettingsService from 'components/hooks/useSettingsService';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { ForceUpdateState, SettingValues } from '@linearb/linjs-common';
import AppRouter from 'common/AppRouter';
import { useHistory } from 'react-router';
import { useEffect, useState } from 'react';
import { settingsSelector } from 'state/selectors/settings';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import { DD_LOGS_CONSTS, LOCAL_STORAGE_KEYS } from 'common/constants/constants';
import { organizationSelector } from 'state/selectors/organization';
import { myAccountSelector } from 'state/selectors/account';
import moment from 'moment';

export const useSettingsLoading = (enabled = false) => {
  const { loadSettings, getSettingOrDefinitionValue, updateSetting } =
    useSettingsService();
  const history = useHistory();
  const organization = useSelector(organizationSelector);
  const account = useSelector(myAccountSelector);
  const settings = useSelector(settingsSelector);
  const localStorageAccessor = BrowserStorageAccessor().local;
  const [forceSystemUpdateFlow, setForceSystemUpdateFlow] = useState(
    ForceUpdateState.NONE
  );
  const [forceLogoutTimestamp, setForceLogoutTimestamp] = useState();

  const loadSettingAndHandleForceSystemUpdate = async () => {
    // Get forceSystemUpdate setting
    const forceSystemUpdateSetting = getSettingOrDefinitionValue(
      {
        name: SettingValues.FORCE_SYSTEM_UPDATE
      },
      {}
    );

    if (forceSystemUpdateSetting?.timestamp) {
      setForceLogoutTimestamp(forceSystemUpdateSetting?.timestamp);

      // Get the login timestamp from the localstorage
      const lastLogin = account?.last_logged_in_at;
      const loginTimestamp = moment(lastLogin).utc().unix();

      const forceLogoutTimestamp = localStorageAccessor.get(
        LOCAL_STORAGE_KEYS.FORCE_LOGOUT_TIMESTAMP
      );

      if (
        !loginTimestamp ||
        loginTimestamp < forceSystemUpdateSetting.timestamp
      ) {
        // If the login timestamp happen in the last hour
        if (
          forceLogoutTimestamp &&
          parseInt(forceLogoutTimestamp) ===
            parseInt(forceSystemUpdateSetting?.timestamp)
        ) {
          // Don't redirect
          window.TrackJS &&
            window.TrackJS.console.info(
              'Login timestamp smaller than force update timestamp, but happen twice'
            );
          window.DD_LOGS &&
            window.DD_LOGS.logger.info(DD_LOGS_CONSTS.force_logout, {
              org_id: organization.id
            });

          await updateSetting(SettingValues.FORCE_SYSTEM_UPDATE, null);
          // Make sure it won't go to force system mode
          setForceSystemUpdateFlow(ForceUpdateState.NONE);
        } else {
          // In case of setting timestamp > login timestamp, or exists setting timestamp but no login timestamp
          setForceSystemUpdateFlow(forceSystemUpdateSetting.uiFlow);

          // Re-route to re-login page
          if (forceSystemUpdateSetting.uiFlow === ForceUpdateState.LOGOUT) {
            history.push(AppRouter.RELOGIN_SYSTEM.path);
          }
        }
      }
    }
  };

  useEffect(() => {
    loadSettingAndHandleForceSystemUpdate();
  }, [settings]);

  const queryData = useQuery(
    [`${AppRouter.SETTINGS.path}/search`],
    // React-query functions need to return something. loadSettings is returning void - so we wrapped it
    async () => {
      await loadSettings();
      return null;
    },
    {
      enabled,
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
      refetchOnWindowFocus: false
    }
  );
  return {
    queryData,
    forceSystemUpdateFlow,
    forceLogoutTimestamp
  };
};
